// Components
import BoundLink from './bound-link'
// Utils
import {accessBallotDisplayType, isBallotScheduledAlreadyEnded, isBallotScheduledNotStarted} from '../utils/ballot-access'
import slugify from '../utils/slugify'
// Types
import type {Ballot, ParentCategory, SubCategory} from '../types/ballot'
// Styles
import styles from './start-button.module.scss'

interface StartButtonProps {
  ballot: Ballot | null
  group?: ParentCategory | null
}

const getFirstQuestionHref = (ballot: Ballot | null, group: ParentCategory | null): string | null => {
  const targetGroup = group || ballot?.parent_categories?.[0] || null
  if (!targetGroup?.parent_category_name) return null

  const allQuestions = (targetGroup.sub_categories || []).filter(
    (question: SubCategory | null | undefined) => Boolean(question?.name),
  )
  const sortedQuestions = (
    targetGroup.sort_order === 'manual'
      ? allQuestions
      : [...allQuestions].sort((a, b) => a?.name?.trim()?.localeCompare(b?.name?.trim()))
  )
  const firstQuestionName = sortedQuestions[0]?.name
  if (!firstQuestionName) return null

  const groupSlug = slugify(targetGroup.parent_category_name)
  const questionSlug = slugify(firstQuestionName)
  if (!groupSlug || !questionSlug) return null

  return `/${groupSlug}/${questionSlug}`
}

export const groupIsNotReady = (ballot: Ballot | null, group: ParentCategory | null): boolean => {
  const targetGroup = group || ballot?.parent_categories?.[0] || null
  if (!targetGroup) return true

  const subcategories = (targetGroup.sub_categories || []).filter(
    (subcat: SubCategory | null | undefined) => Boolean(subcat?.name),
  )

  // No subcategories exist in the group
  if (subcategories.length === 0) return true

  // Nominations are not being accepted and no options are available
  const ballotDisplayType = accessBallotDisplayType(ballot)
  const isAcceptingSubmissions = ballotDisplayType === 'photo-submissions-submit'
  if (!isAcceptingSubmissions) {
    const hasAnyOptions = subcategories.some(
      (subcat: SubCategory) => subcat.final_lists && subcat.final_lists.length > 0,
    )
    if (!hasAnyOptions) return true
  }

  return false
}

const StartButton = ({
  ballot,
  group = null,
}: StartButtonProps) => {
  if (isBallotScheduledAlreadyEnded(ballot)) {
    return (
      <div
        className={`${styles['start-button']} ${styles['start-button--banner']}`}
      >
        Ballot is closed
      </div>
    )
  }

  if (isBallotScheduledNotStarted(ballot)) {
    return (
      <div
        className={`${styles['start-button']} ${styles['start-button--banner']}`}
      >
        Come back soon
      </div>
    )
  }

  const ballotDisplayType = accessBallotDisplayType(ballot)

  let startButtonText = 'Start'
  if (ballotDisplayType === 'photo-voting-results') {
    startButtonText = 'View results now'
  }
  if (ballotDisplayType === 'photo-voting-finalist-announcement') {
    startButtonText = 'View'
  }

  const href = getFirstQuestionHref(ballot, group)
  const isNotReady = groupIsNotReady(ballot, group)

  if (!href || isNotReady) {
    return (
      <button
        className={`button is-dark is-large ${styles['start-button']} ${styles['start-button--disabled']}`}
        disabled
      >
        Coming soon
      </button>
    )
  }

  return (
    <BoundLink
      href={href}
      className={`button is-dark is-large ${styles['start-button']}`}
    >
      {startButtonText}
    </BoundLink>
  )
}

export default StartButton
